import { useEffect, useState } from "react";
import { Building2, Check, Calendar, Phone, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { GOOGLE_MEET_LINK, PHONE, PHONE_HREF, EMAIL } from "@/lib/config";
import { toast } from "sonner";

const field = "w-full bg-charcoal border border-white/15 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold placeholder:text-cream/40";
const BENEFITS = ["Volume & trade pricing", "Dedicated account manager", "Commercial-grade equipment", "White glove installation", "Maintenance & service plans", "Net payment terms available"];

export default function Commercial() {
  const [products, setProducts] = useState([]);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ businessName: "", businessType: "gym", contactName: "", email: "", phone: "", productsInterested: "", quantity: "", timeline: "", message: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
    api.getProducts({ commercial: true }).then((d) => setProducts(d.products || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.postCommercial(form); setDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { toast.error("Something went wrong."); }
  };

  return (
    <div className="pt-16" data-testid="commercial-page">
      {/* Contact bar */}
      <div className="bg-forest/15 border-b border-forest/30" data-testid="trade-contact-bar">
        <div className="max-w-[1100px] mx-auto px-5 py-3 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 text-sm">
          <span className="text-cream/80">Speak with our commercial team directly:</span>
          <div className="flex items-center gap-5">
            <a href={PHONE_HREF} data-testid="trade-phone" className="flex items-center gap-2 text-gold hover:text-amber font-medium">
              <Phone size={15} /> {PHONE}
            </a>
            <a href={`mailto:${EMAIL}`} className="hidden sm:flex items-center gap-2 text-cream/80 hover:text-gold">
              <Mail size={15} /> {EMAIL}
            </a>
            <a href={GOOGLE_MEET_LINK} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-cream/80 hover:text-gold">
              <Calendar size={15} /> Book a Call
            </a>
          </div>
        </div>
      </div>
      {/* Hero */}
      <section className="relative py-28 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1572168400468-62e1b3209d7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Commercial" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-espresso/85" />
        <div className="relative max-w-[1100px] mx-auto px-5">
          <p className="overline text-forest flex items-center gap-2"><Building2 size={16} /> Commercial Solutions</p>
          <h1 className="font-serif text-4xl sm:text-6xl text-cream mt-3 max-w-3xl leading-tight">Outfit Your Facility for Elite Recovery</h1>
          <p className="text-cream/75 mt-5 max-w-2xl text-lg font-light">Gyms, hotels, spas and wellness centres trust us to design, supply and install commercial-grade saunas, cold plunges and hot tubs.</p>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-[1100px] mx-auto px-5 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BENEFITS.map((b) => (
            <div key={b} className="flex items-center gap-3 bg-charcoal border border-white/10 p-4">
              <Check className="text-gold shrink-0" size={18} /><span className="text-cream/85 text-sm">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Commercial products */}
      {products.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-10 pb-16">
          <h2 className="font-serif text-3xl text-cream mb-8">Commercial-Grade Equipment</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Quote form */}
      <section className="max-w-[900px] mx-auto px-5 pb-24">
        <div className="bg-charcoal border border-white/10 p-8 lg:p-12">
          {done ? (
            <div className="text-center py-6" data-testid="commercial-success">
              <div className="w-14 h-14 rounded-full bg-forest/20 flex items-center justify-center mx-auto mb-4"><Check className="text-forest" size={28} /></div>
              <h2 className="font-serif text-3xl text-cream">Quote request received.</h2>
              <p className="text-cream/65 mt-2">Our commercial team will prepare a tailored proposal and reach out shortly.</p>
              <a href={GOOGLE_MEET_LINK} target="_blank" rel="noreferrer" data-testid="commercial-book-link"
                className="mt-6 inline-flex items-center gap-2 bg-gold text-espresso px-6 py-3 font-medium hover:bg-amber transition-colors">
                <Calendar size={18} /> Talk to Our Commercial Team
              </a>
            </div>
          ) : (
            <>
              <p className="overline">Get Started</p>
              <h2 className="font-serif text-3xl text-cream mt-2 mb-6">Request a Quote</h2>
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                <input data-testid="comm-business" required placeholder="Business name" className={field} value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} />
                <select data-testid="comm-type" className={field} value={form.businessType} onChange={(e) => setForm({ ...form, businessType: e.target.value })}>
                  <option value="gym">Gym / Fitness</option><option value="spa">Spa</option><option value="hotel">Hotel / Resort</option><option value="wellness">Wellness Centre</option><option value="other">Other</option>
                </select>
                <input data-testid="comm-name" required placeholder="Contact name" className={field} value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
                <input data-testid="comm-email" required type="email" placeholder="Email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input data-testid="comm-phone" placeholder="Phone" className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <input data-testid="comm-qty" placeholder="Quantity / units" className={field} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                <input data-testid="comm-products" placeholder="Products interested in" className={`${field} sm:col-span-2`} value={form.productsInterested} onChange={(e) => setForm({ ...form, productsInterested: e.target.value })} />
                <input data-testid="comm-timeline" placeholder="Timeline (e.g. Q3 2026)" className={`${field} sm:col-span-2`} value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} />
                <textarea data-testid="comm-message" placeholder="Tell us about your project" rows={3} className={`${field} sm:col-span-2`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <button data-testid="comm-submit" type="submit" className="sm:col-span-2 bg-gold text-espresso py-3.5 font-medium hover:bg-amber transition-colors">Request a Quote</button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
